import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useScrollProgress } from '../hooks';

const LINKS_BEFORE: ReadonlyArray<readonly [to: string, label: string]> = [
  ['/#services', 'What we build'],
];
const LINKS_AFTER: ReadonlyArray<readonly [to: string, label: string]> = [
  ['/#work', 'Case studies'],
  ['/#testimonials', 'Testimonials'],
  ['/#process', 'Process'],
  ['/blog', 'Blog'],
  ['/#faq', 'FAQs'],
];

const PRODUCTS: ReadonlyArray<{ to: string; label: string; desc: string }> = [
  { to: '/lumen', label: 'Lumen ERP', desc: 'Modular cloud ERP' },
  { to: '/pulse', label: 'Pulse', desc: 'WhatsApp agent with AI' },
  { to: '/mindflow', label: 'MindFlow AI', desc: 'Journaling that organizes itself' },
  { to: '/projectflow', label: 'ProjectFlow', desc: 'Project management with an AI PM' },
  { to: '/kinetix', label: 'Kinetix', desc: 'AI project management for agencies' },
];

export function Nav(): React.JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const progress = useScrollProgress();

  const onProductPage = PRODUCTS.some((p) => p.to === pathname);

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on outside click or Escape.
  useEffect(() => {
    if (!open && !productsOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpen(false);
        setProductsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        setProductsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, productsOpen]);

  const closeAll = (): void => {
    setOpen(false);
    setProductsOpen(false);
  };

  return (
    <nav ref={navRef} className={`nav ${scrolled ? 'scrolled' : ''}`}>
      <div className="container nav-inner">
        <Link to="/" className="brand" onClick={closeAll}>
          <img src="/assets/inafana-logo.png" alt="" />
          <span>Inafana</span>
        </Link>

        <button
          type="button"
          className="nav-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="marketing-nav-links"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>

        <div id="marketing-nav-links" className={`nav-links ${open ? 'open' : ''}`}>
          {LINKS_BEFORE.map(([to, label]) => (
            <Link key={to} to={to} onClick={closeAll}>
              {label}
            </Link>
          ))}

          <div className="nav-dropdown">
            <button
              type="button"
              className={`nav-trigger ${onProductPage ? 'active' : ''}`}
              aria-expanded={productsOpen}
              aria-haspopup="true"
              onClick={() => setProductsOpen((v) => !v)}
            >
              Our products
              <ChevronDown className="chev" size={15} aria-hidden="true" />
            </button>

            {productsOpen && (
              <div className="nav-menu">
                {PRODUCTS.map((p) => (
                  <Link key={p.to} to={p.to} onClick={closeAll}>
                    <b>{p.label}</b>
                    <span>{p.desc}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {LINKS_AFTER.map(([to, label]) => (
            <Link key={to} to={to} onClick={closeAll}>
              {label}
            </Link>
          ))}

          <Link to="/#contact" className="btn btn-primary nav-cta" onClick={closeAll}>
            Start a project
          </Link>
        </div>
      </div>
      <div
        className="nav-progress"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />
    </nav>
  );
}
